import { Polygon } from 'geojson';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { FillStyleExtension, getTitilerPgStacLayer, IconLayer, PolygonLayer } from '@methanesat/maps';
import { Theme, ThinCrustSVG, useTheme } from '@methanesat/ui-components';
import { Color } from '@methanesat/colors';

import {
    DEFAULT_TARGET_FILL_COLOR,
    getFilledTargetColor,
    maxZoomPlumeTiles,
    opacityTransition,
    SELECTED_AREA_EMISSION_ICON_PATH
} from '../../../consts';
import { OVERZOOM_ON_USER_INTERACTION, POINT_SOURCES_URL } from '../../../environmentVariables';
import {
    isWithinZoomBounds,
    selectActiveCapture,
    selectCoordinateFromHighlightedFeature,
    selectEmissionsDrawerCardData,
    selectEmissionsMapLayerConfigs,
    selectEmissionsMapName,
    selectFeatureSelected,
    selectFlooredZoom,
    selectHighlightedLayerId,
    selectL4MSATColorScale,
    selectMethaneLayerConfig,
    selectMethaneProduct,
    selectMethaneTargetLayerConfig,
    selectPlatform,
    selectPlumeEmissionRateLayerConfig,
    selectTargetDates
} from '../../../reducers';
import type { RootState } from '../../../store';
import {
    AreaEmissionsProducts,
    CaptureFeature,
    CaptureFeatureProperties,
    CapturePlusCount,
    HighlightedObject,
    MethaneLayerIds,
    Platforms,
    PointSourceStacResponse,
    StacFeature,
    StacSearchResponse,
    GoogleBasemapNames,
    UrlSelectionTypes,
    OGILayerIds
} from '../../../types';
import {
    addRelatedRasterItemIdToPointCaptures,
    componentToString,
    extractPolygonCoords,
    fetchForLayers,
    getCollectionNamesForLayer,
    getColorRangeColors,
    getCOGOpacity,
    getHoveredPlumeId,
    getRasterColorMapName,
    getRectangleBBox,
    getStacCaptureEmissions,
    getStacItemCOGUrl
} from '../../../utils';
import { getCapturesToDisplay, getPointSourceItem } from '../../data/stacData';

type HighlightedObjectWithLayerId = HighlightedObject & {
    layerId: string;
};

/**
 * Adds a selection marker for the selected dispersed area emission or point source.
 * @returns point icon at specific location
 */
export const useDetailsSelectionIcon = () => {
    const highlightedLayerId = useSelector(selectHighlightedLayerId);
    const highlightedFeatureCoordinates = useSelector(selectCoordinateFromHighlightedFeature);
    const flooredZoom = useSelector(selectFlooredZoom);
    const emissionsDrawerCardData = useSelector(selectEmissionsDrawerCardData);
    const {
        'selected-lng': selectedLng,
        'selected-lat': selectedLat,
        'selection-type': selectionType
    } = emissionsDrawerCardData;
    const clickedCoordinates = [selectedLng, selectedLat];

    const iconLocation =
        highlightedFeatureCoordinates?.length === 2 ? highlightedFeatureCoordinates : clickedCoordinates;

    /**
     * Hide the icon when zoomed out past the raster/target threshold, even if a dispersed
     * highlight is still active. The icon should reappear when zooming back in.
     * Also hide the icon when the drawer is showing a target selection. It should only be placed for details selections
     */
    const isVisible = useMemo(
        () =>
            isWithinZoomBounds(flooredZoom, MethaneLayerIds.areaEmissionRaster) &&
            highlightedLayerId !== OGILayerIds.tileInfrastructure &&
            selectionType === UrlSelectionTypes.Details,
        [flooredZoom, highlightedLayerId, selectionType]
    );

    const ICON_MAPPING = {
        marker: { x: 0, y: 0, width: 58, height: 58 }
    };

    return new IconLayer({
        id: 'selected-emission-details-icon-layer',
        data: iconLocation,
        getIcon: () => 'marker',
        getPosition: () => iconLocation as [number, number],
        getPixelOffset: [0, 0], // this coordinate pair moves the point of the icon to align with the user's click
        getSize: 50,
        iconAtlas: SELECTED_AREA_EMISSION_ICON_PATH,
        iconMapping: ICON_MAPPING,
        visible: isVisible
    });
};

/** Sorts the selected raster to the end of the deck.gl layer array, so that it can be rendered above others */
const sortSelectedRasterLast = (
    featureA: CapturePlusCount,
    featureB: CapturePlusCount,
    selectedItemId: string,
    selectedTargetId: string | undefined
) => {
    if (selectedItemId === '' || selectedTargetId === undefined) return 0;

    // determine which feature is selected
    const aIsSelected =
        featureA.id === selectedItemId ||
        featureA.properties.target_group_id === selectedTargetId ||
        featureA.properties.target_id === selectedTargetId;
    const bIsSelected =
        featureB.id === selectedItemId ||
        featureB.properties.target_group_id === selectedTargetId ||
        featureB.properties.target_id === selectedTargetId;

    // if A is selected, A should be moved to the end of the array (top of the layer stack)
    if (aIsSelected) return 1;
    // if B is selected, B should be moved to the end of the the array
    if (bIsSelected) return -1;
    return 0;
};

/**
 * Get historic raster data as specified by given date range.
 * @param colormap
 * @param zoom
 * @returns Array of raster layers
 */
export const useMethaneRasterLayers = (
    data: StacSearchResponse<Polygon, CaptureFeatureProperties> | null,
    isInteracting: boolean
) => {
    const targetDates = useSelector(selectTargetDates);
    const platform = useSelector(selectPlatform);
    const product = useSelector(selectMethaneProduct);
    const selectedFeatureMethane = useSelector(selectFeatureSelected).selectedMethaneFeature;
    const selectedTargetId = useSelector(selectActiveCapture)?.targetId;

    const mapName = useSelector(selectEmissionsMapName);
    const l4MSATColorScale = useSelector(selectL4MSATColorScale);

    const enabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.areaEmissionRaster].enabled
    );
    const flooredZoom = useSelector(selectFlooredZoom);
    const { ...targetLayerProps } = useSelector(selectMethaneTargetLayerConfig);

    // captures/items from STAC. These are used to get the COG urls
    // for rendering in the raster layers
    const [dataToDisplay, setDataToDisplay] = useState<CapturePlusCount[]>([]);
    const [dataWithTotalsOnly, setDataWithTotalsOnly] = useState<CapturePlusCount[]>([]);

    // Manages cancellation of in-flight raster requests when the layer should no longer be drawn
    const abortControllerRef = useRef<AbortController | null>(null);

    const collectionNames = useMemo(() => {
        return getCollectionNamesForLayer(product, platform, MethaneLayerIds.areaEmissionRaster);
    }, [product, platform]);

    const shouldDraw = useMemo(
        () => isWithinZoomBounds(flooredZoom, MethaneLayerIds.areaEmissionRaster),
        [flooredZoom]
    );

    // Create or clear the AbortController based on draw state
    useEffect(() => {
        if (shouldDraw) {
            abortControllerRef.current = new AbortController();
        } else {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
        }
    }, [shouldDraw]);

    // Load raster data only when ready
    useEffect(() => {
        if (data && shouldDraw) {
            const capturesToDisplay = getCapturesToDisplay(data.features, targetDates);
            if (capturesToDisplay) {
                // Data used for the raster layers
                setDataToDisplay(capturesToDisplay[product]);

                // Data with totals only. A striped fill pattern is shown for these captures
                setDataWithTotalsOnly(
                    capturesToDisplay.l4.filter((c) => !c.collection.includes('point') && !c.assets?.COG)
                );
            }
        }
    }, [data, product, targetDates, shouldDraw]);

    /**
     * Creates an array of raster tile layers,
     * one for each selected target-capture id
     */
    const rasterLayers = dataToDisplay
        .filter((feature) => collectionNames.includes(feature.collection) && feature.assets?.COG)
        .sort((a, b) => sortSelectedRasterLast(a, b, selectedFeatureMethane.itemId, selectedTargetId))
        .map((feature) => {
            const {
                id: itemId,
                properties: { target_id: parentTargetId, target_group_id: targetGroupId, pixel_size_km2: pixelSizeKm2 }
            } = feature;

            /**
             * `selected` is used to determine COG opacity. If anything is selected,
             * all other COGs get additional transparency. If nothing is selected, all COGs
             * get the default/selected treatment.
             */
            const selected =
                selectedFeatureMethane.itemId === '' ||
                selectedTargetId === null ||
                targetGroupId === selectedTargetId ||
                parentTargetId === selectedTargetId;

            const colormapName = getRasterColorMapName(platform, product, l4MSATColorScale, !selected);
            const featureBbox = getRectangleBBox(feature.geometry.coordinates[0]);
            const dataUrl = getStacItemCOGUrl(itemId, feature.collection, colormapName, platform, pixelSizeKm2);
            const zoom = OVERZOOM_ON_USER_INTERACTION && isInteracting ? Math.max(flooredZoom - 1, 0) : flooredZoom;
            const opacity = getCOGOpacity({
                zoom: Math.floor(zoom),
                dimmed: !selected,
                mapName,
                platform
            });

            return getTitilerPgStacLayer({
                dataUrl,
                id: `${MethaneLayerIds.areaEmissionRaster}_${itemId}`,
                zoom,
                opacity,
                TileLayerProps: {
                    fetch: (url, params) =>
                        fetchForLayers(url, {
                            ...params,
                            visibilitySignal: abortControllerRef.current?.signal
                        })
                },
                bounds: featureBbox,
                itemId,
                targetId: parentTargetId,
                collectionId: feature.collection,
                visible: shouldDraw && enabled
            });
        });

    /** Renders a fill over captures with no raster data */
    const totalsOnlyStripeLayer = new PolygonLayer({
        ...targetLayerProps,
        id: `${MethaneLayerIds.areaEmissionRaster}-total-only-fill`,
        filled: true,
        data: dataWithTotalsOnly,
        updateTriggers: { getFillColor: [selectedFeatureMethane, selectedTargetId] },
        getFillColor: (d) => {
            const {
                properties: { target_id: parentTargetId }
            } = d;

            /**
             * `selected` is used to determine if greyscale colors are used. If any other emissions data is selected,
             * return greyscale. If nothing is selected, default colors.
             */
            const selected =
                selectedFeatureMethane.itemId === '' ||
                selectedTargetId === null ||
                parentTargetId === selectedTargetId;

            const totalMethane = getStacCaptureEmissions(d.properties).totalKgHr;
            const hasTotal = !!totalMethane;

            // lighter grey is returned for 'satellite' to improve visibility
            const greyscaleHatch =
                mapName === GoogleBasemapNames.Roadmap
                    ? ([194, 193, 190, 255] as Color)
                    : ([230, 230, 230, 255] as Color);

            // if not selected while another feature is selected, return greyscale colors
            if (!selected) {
                return hasTotal ? greyscaleHatch : DEFAULT_TARGET_FILL_COLOR;
            }

            return hasTotal ? getFilledTargetColor(totalMethane, platform) : DEFAULT_TARGET_FILL_COLOR;
        },
        getPolygon: (d) => extractPolygonCoords(d),
        pickable: true,
        stroked: true,
        visible: shouldDraw && enabled && product === AreaEmissionsProducts.l4,
        fillPatternAtlas: '/img/diagonal-hatch.svg',
        fillPatternMapping: {
            diagonalHatch: {
                width: 256,
                height: 256,
                mask: true
            }
        },
        getFillPattern: () => 'diagonalHatch',
        getFillPatternScale: 120,
        extensions: [new FillStyleExtension({ pattern: true })]
    });

    return [totalsOnlyStripeLayer, ...rasterLayers];
};

/**
 * Get historic raster data for one target as specified by given date range.
 * @param data
 * @param zoom
 * @returns Array of raster layers
 */
export const useOneMethaneRasterLayer = (
    data: StacFeature<Polygon, CaptureFeatureProperties>,
    zoom: number,
    platform: Platforms,
    product: AreaEmissionsProducts,
    mapName: GoogleBasemapNames
) => {
    const l4MSATColorScale = useSelector(selectL4MSATColorScale);
    const colormapName = getRasterColorMapName(platform, product, l4MSATColorScale);

    // Manages cancellation of in-flight raster requests when the layer should no longer be drawn
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        abortControllerRef.current = new AbortController();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [data]);

    /**
     * Returns one raster tile
     */

    const {
        id: itemId,
        properties: { target_id: parentTargetId, pixel_size_km2: pixelSizeKm2 }
    } = data;

    const featureBbox = getRectangleBBox(data.geometry.coordinates[0]);
    const dataUrl = getStacItemCOGUrl(itemId, data.collection, colormapName, platform, pixelSizeKm2);
    const opacity = getCOGOpacity({
        zoom: Math.floor(zoom),
        dimmed: false,
        mapName,
        platform
    });

    return getTitilerPgStacLayer({
        dataUrl,
        id: `${MethaneLayerIds.areaEmissionRaster}_${itemId}`,
        zoom,
        opacity,
        TileLayerProps: {
            fetch: (url, params) =>
                fetchForLayers(url, {
                    ...params,
                    visibilitySignal: abortControllerRef.current?.signal
                })
        },
        bounds: featureBbox,
        itemId,
        targetId: parentTargetId,
        collectionId: data.collection,
        visible: true
    });
};

/**
 * Helper function to cache and retrieve the url for the thin crust svg. This is used
 * to avoid issues with immediately calling componentToString from a hook.
 */
const makeThinCrustUrl = (theme: Theme) => {
    let thinCrustUrl: string | undefined;
    return () => {
        if (!thinCrustUrl) {
            thinCrustUrl = `data:image/svg+xml;base64,${btoa(componentToString(<ThinCrustSVG colors={getColorRangeColors(theme, true)} highlighted={false} height={100} width={100} />))}`;
        }
        return thinCrustUrl;
    };
};

/**
 * Helper function to cache and retrieve the url for the highlighted thin crust svg. This is used
 * to avoid issues with immediately calling componentToString from a hook.
 */
const makeHighlightedThinCrustUrl = (theme: Theme) => {
    let thinCrustUrl: string | undefined;
    return () => {
        if (!thinCrustUrl) {
            thinCrustUrl = `data:image/svg+xml;base64,${btoa(componentToString(<ThinCrustSVG colors={getColorRangeColors(theme, true)} highlighted={true} height={100} width={100} />))}`;
        }
        return thinCrustUrl;
    };
};

// 112 is a multiple of the current 28 x 28 icon. The
// plume icon isn't expected to get bigger than this.
const SVG_ICON_SIZE = 112;

// DP-5345 - replace the old url with the new pattern
const oldUrlPattern =
    /((gs:\/\/)|(https:\/\/storage.googleapis.com\/msat-(dev|stag|prod)-portal-point-sources-public))/i;
export const replaceUrlSource = (oldUrl: string) => {
    return oldUrl.replace(oldUrlPattern, `${window.location.origin}${POINT_SOURCES_URL}`);
};

/**
 * Builds the deck.gl layer for plume emissions rates.
 */
export const usePlumeEmissionRateLayer = (data: StacSearchResponse<Polygon, CaptureFeatureProperties> | null) => {
    const theme = useTheme();
    const targetDates = useSelector(selectTargetDates);
    const layerProps = useSelector(selectPlumeEmissionRateLayerConfig);
    const dispersedEmissionsEnabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.areaEmissionRaster].enabled
    );

    const [pointSourceFeatureCollections, setPointSourceFeatureCollections] = useState<
        (PointSourceStacResponse | null)[]
    >([]);

    const highlightedFeatureId = useSelector(
        (state: RootState) =>
            selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.plumeEmissionRate].highlightedFeatureId
    );
    const hoveredPlumeId = useSelector(
        (state: RootState) => selectMethaneLayerConfig(state)[MethaneLayerIds.plumeEmissionRate].hoveredPlumeId
    );

    const getThinCrustUrl = useMemo(() => makeThinCrustUrl(theme), [theme]);

    const getHighlightedThinCrustUrl = useMemo(() => makeHighlightedThinCrustUrl(theme), [theme]);

    // Load Point Source data only when ready, or the data changes
    useEffect(() => {
        const abortController = new AbortController();
        if (data && layerProps.visible) {
            // Plumes are a level 4 product
            const captures = addRelatedRasterItemIdToPointCaptures(
                getCapturesToDisplay(data.features, targetDates)[AreaEmissionsProducts.l4]
            );

            Promise.all(
                captures.map((feature) => {
                    const pointSourceUrl = feature.assets?.GeoJSON?.href ?? feature.assets?.growing_box_pruned?.href;
                    const updatedPointSourceUrl = pointSourceUrl && replaceUrlSource(pointSourceUrl);

                    return getPointSourceItem({
                        pointItemId: feature.id,
                        relatedRasterItemId: feature.relatedRasterItemId,
                        stacCollectionId: feature.collection,
                        targetId: feature.properties.target_id,
                        pointSourceUrl: updatedPointSourceUrl,
                        abortController
                    });
                })
            ).then((pointSources) => {
                setPointSourceFeatureCollections(pointSources);
            });
        }
        // cleanup
        return () => {
            abortController.abort('Hook cleanup');
        };
    }, [data, targetDates, layerProps.visible]);

    const target = useSelector(selectActiveCapture)?.targetId;

    // Return the deck.gl layers for plumes - one layer for each feature collection
    return pointSourceFeatureCollections
        ?.filter((pointSourceData) => !!pointSourceData)
        .map((pointSourceData) => {
            const plumeIconLayerId = `${MethaneLayerIds.plumeEmissionRate}_${pointSourceData.id}`;
            const hasSelectedTarget = target != null;
            const isCurrentTarget = target === pointSourceData.parentTargetId;
            const keep100Opacity = !hasSelectedTarget || isCurrentTarget || !dispersedEmissionsEnabled;

            return new IconLayer({
                ...layerProps,
                data: pointSourceData.features?.map((feature) => {
                    return {
                        ...feature,
                        itemId: pointSourceData.id,
                        collection: pointSourceData.collection,
                        parentTargetId: pointSourceData.parentTargetId,
                        relatedRasterItemId: pointSourceData.relatedRasterItemId,
                        processorVersion: pointSourceData.metadata?.processor_version
                    };
                }),
                fetch: fetchForLayers,
                // Return an object that represents the url for the svg and the max width
                // and max height for the image. It should probably the be same as the resizeWidth
                // and resizeHeight above.
                // https://deck.gl/docs/api-reference/layers/icon-layer#geticon
                getIcon: (d) => {
                    const isClicked =
                        (layerProps.highlightedObject as HighlightedObjectWithLayerId)?.layerId === plumeIconLayerId &&
                        highlightedFeatureId === d.properties.plume_id;
                    const isHovered = hoveredPlumeId === getHoveredPlumeId(d);
                    if (isClicked || isHovered) {
                        return {
                            url: getHighlightedThinCrustUrl(),
                            width: SVG_ICON_SIZE,
                            height: SVG_ICON_SIZE
                        };
                    }
                    return {
                        url: getThinCrustUrl(),
                        width: SVG_ICON_SIZE,
                        height: SVG_ICON_SIZE
                    };
                },
                textureParameters: {
                    mipmapFilter: 'linear' // sharpens the icon a bit
                },
                getPosition: (d) => {
                    return d.geometry.coordinates;
                },
                getColor: (_d) => {
                    const alpha = keep100Opacity ? 255 : 0.2 * 255;
                    return [255, 0, 0, alpha];
                },
                highlightColor: [0, 0, 0, 0],
                id: plumeIconLayerId,
                // When loading the icon, it'll resize the image to be of the certain width
                // and height
                // https://loaders.gl/docs/modules/images/api-reference/image-loader#imagebitmap-options
                loadOptions: {
                    imagebitmap: {
                        resizeWidth: SVG_ICON_SIZE,
                        resizeHeight: SVG_ICON_SIZE,
                        resizeQuality: 'high'
                    }
                },
                maxZoom: maxZoomPlumeTiles,
                pointType: 'icon',
                uniqueIdProperty: 'plume_id',
                updateTriggers: { getIcon: [highlightedFeatureId, hoveredPlumeId] },
                visible: layerProps.visible,
                opacity: layerProps.visible ? 1 : 0,
                transitions: {
                    opacity: opacityTransition
                }
            });
        });
};

/**
 * Creates the plume layer for a singular data capture
 * @param data The capture (`point_pruned` STAC item) that you want to create the plume layer for
 * @returns deck.gl `IconLayer`
 */
export const useOnePlumeEmissionRateLayer = (data: CaptureFeature | null) => {
    const theme = useTheme();
    const layerProps = useSelector(selectPlumeEmissionRateLayerConfig);

    const [pointSourceData, setPointSourceData] = useState<PointSourceStacResponse | null>();

    const getThinCrustUrl = useMemo(() => makeThinCrustUrl(theme), [theme]);

    useEffect(() => {
        const abortController = new AbortController();
        if (data) {
            getPointSourceItem({
                pointItemId: data.id,
                relatedRasterItemId: '',
                stacCollectionId: data.collection,
                targetId: data.properties.target_id,
                pointSourceUrl: data.assets?.GeoJSON?.href ?? data.assets?.growing_box_pruned?.href,
                abortController
            }).then((item) => {
                setPointSourceData(item);
            });
        }
        // cleanup
        return () => {
            abortController.abort('Hook cleanup');
        };
    }, [data]);

    return new IconLayer({
        ...layerProps,
        data: pointSourceData
            ? pointSourceData?.features?.map((feature) => {
                  return {
                      ...feature,
                      itemId: pointSourceData.id,
                      collection: pointSourceData.collection,
                      parentTargetId: pointSourceData.parentTargetId,
                      relatedRasterItemId: pointSourceData.relatedRasterItemId
                  };
              })
            : [],
        fetch: fetchForLayers,
        // Return an object that represents the url for the svg and the max width
        // and max height for the image. It should probably the be same as the resizeWidth
        // and resizeHeight above.
        // https://deck.gl/docs/api-reference/layers/icon-layer#geticon
        getIcon: () => {
            return {
                url: getThinCrustUrl(),
                width: SVG_ICON_SIZE,
                height: SVG_ICON_SIZE
            };
        },
        textureParameters: {
            mipmapFilter: 'linear' // sharpens the icon a bit
        },
        getPosition: (d) => {
            return d.geometry.coordinates;
        },
        getColor: (_d) => {
            const alpha = 255;
            return [255, 0, 0, alpha];
        },
        highlightColor: [0, 0, 0, 0],
        id: `${MethaneLayerIds.plumeEmissionRate}`,
        // When loading the icon, it'll resize the image to be of the certain width
        // and height
        // https://loaders.gl/docs/modules/images/api-reference/image-loader#imagebitmap-options
        loadOptions: {
            imagebitmap: {
                resizeWidth: SVG_ICON_SIZE,
                resizeHeight: SVG_ICON_SIZE,
                resizeQuality: 'high'
            }
        },
        maxZoom: maxZoomPlumeTiles,
        pointType: 'icon',
        uniqueIdProperty: 'plume_id',
        visible: true,
        opacity: 1,
        transitions: {
            opacity: opacityTransition
        }
    });
};
